import type { FeatureFlags, MeResponse } from '@ucp/web/lib';
import { ENTERPRISE_ROLES, SCHOOL_ROLES } from '../insights/insightsRoles';
import { ofstedModuleEnabled } from '../ofsted/ofstedRoles';
import { hasAnyRole } from './navConfig';
import {
  effectiveModuleStatus,
  entitledChrome,
  portalOpsConfig,
  visibleModules,
} from './modulesConfig';

/**
 * MF-01 (PRO-0600 Increment 2 / ADR-014 A8) — the post-auth landing target.
 *
 * TOTAL and provably never returns a guarded route the caller cannot enter: `/`
 * (Comms Home, ungated) is the universal safe fallback. Only ever called for
 * CONFIGURED users (roles or workforce baseline). Zero-role users without admission
 * never reach the router (they render
 * `AccessNotConfiguredView` upstream in `AccessGate`, which carries the ADR-038 A1-1
 * role-free landing).
 *
 * Branch ordering (SF-001):
 *   1. Insight lens — Enterprise then School — ONLY when `insightsEnabled`, and only
 *      for a caller who holds the lens role (so the `/insights/*` guard passes). The
 *      role sets are re-used from `insightsRoles` so this can never drift from the
 *      lens gating. NOTE the PRO-0610 baseline `SCHOOL_STAFF` is inside `SCHOOL_ROLES`
 *      → a baseline caller lands on `/insights/school` (server narrows the KPI set).
 *   2. L6-only platform admin (holds `L6_PLATFORM_ADMIN` and NO business role — EoP,
 *      no insights capability) → the Settings/platform-ops surface.
 *   3. PRO-1360 / ADR-014 A14 — the caller's first ENTITLED chrome destination, when
 *      `navEntitlementFilterEnabled` is on. Appended after the two branches above so
 *      they stay byte-for-byte, and it exists because `/` is not a safe fallback under
 *      entitlement-first chrome: a caller with no Comms entitlement lands on a page
 *      whose every link has just been removed. This strengthens the contract from
 *      "never a guarded route the caller cannot enter" to "always an entitled route".
 *   4. `/` universal fallback for every remaining configured user (incl. an
 *      `L6 + <business role>` user, who took a richer landing at step 1 or falls to
 *      `/` here — never step 2).
 *
 * `insightsEnabled` is a SEMANTIC input and must be the real flag: callers that pass a
 * composed enablement boolean here will route `SCHOOL_ROLES` (which includes the
 * baseline `SCHOOL_STAFF`) to an `/insights/*` route that `AppRoutes` has not mounted.
 */
export function landingRouteForUser(
  user: MeResponse,
  insightsEnabled: boolean,
  flags?: Partial<FeatureFlags>,
): string {
  if (insightsEnabled) {
    if (hasAnyRole(user, ENTERPRISE_ROLES)) return '/insights/enterprise';
    if (hasAnyRole(user, SCHOOL_ROLES)) return '/insights/school';
  }
  if (hasAnyRole(user, ['L6_PLATFORM_ADMIN']) && !hasBusinessLanding(user)) {
    return portalOpsConfig[0]?.defaultPath ?? '/';
  }
  if (flags?.navEntitlementFilterEnabled === true) {
    const entitled = entitledChrome(
      visibleModules(
        insightsEnabled,
        flags.commercialDataEnabled ?? false,
        flags.attendanceEnabled === true,
        flags.tripsEnabled === true,
        flags.opsIssuesEnabled === true,
        ofstedModuleEnabled(flags),
      ),
      user,
      flags,
    );
    for (const module of entitled) {
      // A module that is only effectively coming-soon (HR, Finance) renders a
      // placeholder dashboard at its basePath and that path is deliberately not a nav
      // target (ADR-014 A9), so land on the first entitled child instead. Complaints
      // and Referrals are in this set only when their flag has promoted them, at which
      // point their basePath is a real mounted route.
      const promoted =
        effectiveModuleStatus(module, flags.complaintsEnabled, flags.referralsEnabled) === 'active';
      if (promoted) {
        return module.basePath;
      }
      const first = module.subModules?.[0];
      if (first) {
        return first.basePath;
      }
    }
  }
  if (user.workforceBaseline?.admitted === true && user.roles.length === 0) {
    if (flags?.hrAssistantEnabled === true) return '/hr/people-assistant';
    if (flags?.policyManagementEnabled === true) return '/hr/policies';
    return '/welcome';
  }
  return '/';
}

/**
 * SF-001 — the caller holds any role OTHER than `L6_PLATFORM_ADMIN` (i.e. has a real
 * business landing), so the L6-only branch must not claim them.
 */
function hasBusinessLanding(user: MeResponse): boolean {
  return user.roles.some((assignment) => assignment.role !== 'L6_PLATFORM_ADMIN');
}
